/**
 * Oyun koordinatı ↔ harita dünyası dönüşümü.
 *
 * Hesap makinesi iki koordinat sistemi arasında gider gelir:
 *
 * - **Oyun koordinatı** — oyuncunun oyunda okuduğu sayı (`x72.45, y31.20`).
 *   Birimi 100 m'dir (`metersPerUnit`); ekranda iki ondalıkla, yani metre
 *   hassasiyetinde yazılır. Kullanıcının gördüğü ve kopyaladığı biçim budur.
 * - **Dünya** — `MapTileLayer`'ın koordinat uzayı. Burada **1 dünya birimi =
 *   1 metre** olarak seçildi, yani menzil çemberinin yarıçapı doğrudan silahın
 *   metre cinsinden menzilidir; ölçek çevirisi hiçbir çizim yolunda geçmez.
 *
 * Dünyanın kenar uzunluğunu tile piramidi belirler: z=0'daki tek tile tam
 * olarak `tileBounds` dikdörtgenini gösterir (üç haritada da 163.84 birim =
 * 16.384 m). Y ekseni ters çevrilir — oyun koordinatı yukarı doğru büyür,
 * ekran aşağı doğru.
 *
 * Kalibrasyon değerleri katalogdan gelir (`backend/strategies/maps.py`).
 */

/** Oyun koordinatı cinsinden dikdörtgen. */
export interface GameRect {
  minX: number
  maxX: number
  minY: number
  maxY: number
}

export interface GameCalibration {
  /** Oynanabilir alan; piramit bunun dışını da kapsar. */
  bounds: GameRect
  /** Tile piramidinin kapladığı alan. */
  tileBounds: GameRect
  /** Bir koordinat biriminin metre karşılığı (Wardogs'ta 100). */
  metersPerUnit: number
}

/** Oyun koordinatı cinsinden nokta. */
export interface GamePoint {
  x: number
  y: number
}

/** Dünya (metre) cinsinden nokta — `MapTileLayer` uzayı. */
export interface WorldPoint {
  x: number
  y: number
}

/** Dünyanın kenar uzunluğu, metre. */
export function worldSize(cal: GameCalibration): number {
  return (cal.tileBounds.maxX - cal.tileBounds.minX) * cal.metersPerUnit
}

export function toWorld(p: GamePoint, cal: GameCalibration): WorldPoint {
  return {
    x: (p.x - cal.tileBounds.minX) * cal.metersPerUnit,
    y: (cal.tileBounds.maxY - p.y) * cal.metersPerUnit,
  }
}

export function toGame(p: WorldPoint, cal: GameCalibration): GamePoint {
  return {
    x: cal.tileBounds.minX + p.x / cal.metersPerUnit,
    y: cal.tileBounds.maxY - p.y / cal.metersPerUnit,
  }
}

/**
 * Noktayı oynanabilir alana çeker ve metre hassasiyetine yuvarlar.
 *
 * Yuvarlama görüntüyle ilgili değil: kullanıcı iki ondalıklı bir koordinat
 * görüyor, dolayısıyla sürükleyerek koyduğu nokta da tam o değerde durmalı —
 * yoksa ekranda yazan sayı ile hesaba giren sayı birbirinden ayrılır.
 */
export function clampToBounds(p: GamePoint, cal: GameCalibration): GamePoint {
  const step = cal.metersPerUnit   // 100 birim-alt-adım = 1 m
  const snap = (v: number) => Math.round(v * step) / step
  return {
    x: Math.min(cal.bounds.maxX, Math.max(cal.bounds.minX, snap(p.x))),
    y: Math.min(cal.bounds.maxY, Math.max(cal.bounds.minY, snap(p.y))),
  }
}

/** İki oyun koordinatı arasındaki yatay mesafe, metre. */
export function distanceMeters(a: GamePoint, b: GamePoint, cal: GameCalibration): number {
  return Math.hypot(b.x - a.x, b.y - a.y) * cal.metersPerUnit
}

/**
 * Bataryadan hedefe pusula açısı, derece (0 = kuzey, saat yönünde artar).
 *
 * `atan2(dx, dy)` — bilinen `atan2(y, x)` değil: kuzeyi sıfır kabul eden bir
 * pusula açısı isteniyor, matematiksel açı değil.
 */
export function azimuthDeg(a: GamePoint, b: GamePoint): number {
  const deg = (Math.atan2(b.x - a.x, b.y - a.y) * 180) / Math.PI
  return deg < 0 ? deg + 360 : deg
}

/** Kullanıcıya gösterilen koordinat biçimi: metre hassasiyetinde iki ondalık. */
export function formatGameCoord(value: number): string {
  return value.toFixed(2)
}

/** Bir noktanın kopyalanabilir metni — orijinal hesap makinesiyle aynı biçim. */
export function pointText(p: GamePoint): string {
  return `x${formatGameCoord(p.x)}, y${formatGameCoord(p.y)}`
}

/**
 * `x72.45, y31.20` benzeri bir metinden koordinat okur.
 *
 * Etiketli biçim (`x…`, `y…`) ve iki çıplak sayı kabul edilir; ondalık ayırıcı
 * virgül de olabilir. Bu, wardogs-artillery.com'un kopyaladığı biçimle aynı —
 * iki araç arasında koordinat elle yeniden yazılmasın diye.
 */
export function parsePointText(value: string): GamePoint | null {
  const text = String(value ?? '').trim()
  if (!text) return null

  const number = '[+-]?\\d+(?:[.,]\\d+)?'
  const num = (raw: string) => Number(raw.replace(',', '.'))

  const xMatch = text.match(new RegExp(`(?:^|[^a-z])x\\s*[:=]?\\s*(${number})`, 'i'))
  const yMatch = text.match(new RegExp(`(?:^|[^a-z])y\\s*[:=]?\\s*(${number})`, 'i'))

  if (xMatch && yMatch) {
    const x = num(xMatch[1])
    const y = num(yMatch[1])
    return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null
  }

  const numbers = text.match(new RegExp(number, 'g'))
  if (!numbers || numbers.length !== 2) return null

  const x = num(numbers[0])
  const y = num(numbers[1])
  return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null
}

/** Oynanabilir alanın ortası — nokta yerleştirilmemişken başlangıç noktası. */
export function boundsCenter(cal: GameCalibration): GamePoint {
  return {
    x: (cal.bounds.minX + cal.bounds.maxX) / 2,
    y: (cal.bounds.minY + cal.bounds.maxY) / 2,
  }
}
